import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { formsApi } from "../../services/api";
import { useTranslation } from "../../contexts/TranslationContext";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorAlert from "../common/ErrorAlert";
import FormHeader from "./detail/FormHeader";
import FormStats from "./detail/FormStats";
import FormConfiguration from "./detail/FormConfiguration";
import FormVariations from "./detail/FormVariations";
import FormEmbedCode from "./detail/FormEmbedCode";
import FormSubmissionsTable from "./detail/FormSubmissionsTable";
import { config } from "../../config";

const FormDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: form,
    isLoading,
    error,
  } = useQuery(["form", id], () => formsApi.getForm(Number(id)), {
    enabled: !!id,
  });

  const { data: submissionsData } = useQuery(
    ["form-submissions", id, 1],
    () => formsApi.getFormSubmissions(Number(id), 0, 1, "submitted_at", "desc"),
    { enabled: !!id },
  );

  const deleteFormMutation = useMutation(formsApi.deleteForm, {
    onSuccess: () => {
      queryClient.invalidateQueries("forms");
      navigate("/forms");
    },
  });

  const toggleFormMutation = useMutation(
    (isActive: boolean) =>
      formsApi.updateForm(Number(id), { is_active: isActive }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["form", id]);
        queryClient.invalidateQueries("forms");
      },
    },
  );

  const handleDelete = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this form? This action cannot be undone.",
      )
    ) {
      await deleteFormMutation.mutateAsync(Number(id));
    }
  };

  const handleToggleStatus = async () => {
    if (form) {
      await toggleFormMutation.mutateAsync(!form.is_active);
    }
  };

  const handleTestForm = async () => {
    try {
      const html = await formsApi.getFormTestHtml(Number(id));
      const newWindow = window.open("", "_blank");
      if (newWindow) {
        newWindow.document.write(html);
        newWindow.document.close();
      }
    } catch (error) {
      console.error("Failed to load form test:", error);
      alert("Failed to load form test. Please try again.");
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !form) {
    return <ErrorAlert message={t("messages.formNotFound")} />;
  }

  const totalSubmissions = submissionsData?.stats?.total_submissions || 0;
  const completedPayments = submissionsData?.stats?.completed_payments || 0;
  const totalRevenue = submissionsData?.stats?.total_revenue || 0;

  return (
    <div className="space-y-6">
      <FormHeader
        form={form}
        onToggleStatus={handleToggleStatus}
        onTest={handleTestForm}
        onDelete={handleDelete}
        isToggling={toggleFormMutation.isLoading}
        isDeleting={deleteFormMutation.isLoading}
      />

      <FormStats
        totalSubmissions={totalSubmissions}
        completedPayments={completedPayments}
        totalRevenue={totalRevenue}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FormConfiguration form={form} />
        <FormVariations variations={form.variations} />
      </div>

      <FormEmbedCode formId={form.id} apiBaseUrl={config.API_BASE_URL} />

      <FormSubmissionsTable
        formId={Number(id)}
        variations={form.variations}
        itemsPerPage={10}
      />
    </div>
  );
};

export default FormDetail;
